import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  type MotionValue,
} from "framer-motion";
import { LeverScene } from "./LeverScene";
import { useAuthStore } from "../../stores/auth";
import { APP_NAME, LeverMark } from "../../lib/brand";

const EASE_EXPO: [number, number, number, number] = [0.16, 1, 0.3, 1];

function useScrollMoment(
  progress: MotionValue<number>,
  enter: number,
  exit: number,
  dur = 0.08,
) {
  return {
    opacity: useTransform(
      progress,
      [enter, enter + dur, exit - dur, exit],
      [0, 1, 1, 0],
    ),
  };
}

function useScrollEnter(
  progress: MotionValue<number>,
  enter: number,
  dur = 0.08,
) {
  return {
    opacity: useTransform(progress, [enter, enter + dur], [0, 1]),
  };
}

/* ── Overlays ── */

function FilmGrain() {
  return (
    <div
      className="pointer-events-none fixed inset-0 z-[2] opacity-[0.04]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundSize: "256px 256px",
        mixBlendMode: "overlay",
      }}
    />
  );
}

function Vignette() {
  return (
    <div
      className="pointer-events-none fixed inset-0 z-[1]"
      style={{
        background:
          "radial-gradient(ellipse 70% 60% at 50% 50%, transparent 0%, rgba(0,0,0,0.4) 100%)",
      }}
    />
  );
}

/* ── Data ── */

const CAPS = [
  {
    label: "01",
    title: "Write",
    desc: "LaTeX and Markdown in a unified editor. Live preview. Instant compilation.",
  },
  {
    label: "02",
    title: "Think",
    desc: "AI that understands your document. Inline rewrites. Ghost-text completions.",
  },
  {
    label: "03",
    title: "Collaborate",
    desc: "Real-time multiplayer. Comments. Version history. Shared workspaces.",
  },
];

/* ── Page ── */

export function LandingPage() {
  const navigate = useNavigate();
  const { user, initialized } = useAuthStore();
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const scrollRef = useRef({ current: 0 });

  useEffect(() => {
    if (initialized && user) {
      navigate("/", { replace: true });
    }
  }, [initialized, user, navigate]);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("lever-landing-active");
    return () => document.documentElement.classList.remove("lever-landing-active");
  }, []);

  const { scrollY, scrollYProgress } = useScroll();

  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);
  const heroY = useTransform(scrollY, [0, 500], [0, -80]);
  const navOpacity = useTransform(scrollY, [200, 400], [0, 1]);

  const s1 = useScrollMoment(scrollYProgress, 0.04, 0.28);
  const s2 = useScrollMoment(scrollYProgress, 0.16, 0.40);

  const c1 = useScrollMoment(scrollYProgress, 0.30, 0.54);
  const c2 = useScrollMoment(scrollYProgress, 0.42, 0.64);
  const c3 = useScrollMoment(scrollYProgress, 0.54, 0.74);

  const quote = useScrollMoment(scrollYProgress, 0.66, 0.94);

  const cta = useScrollEnter(scrollYProgress, 0.92);

  useMotionValueEvent(scrollY, "change", (v) => {
    setScrolled(v > 20);
    setPastHero(v > 300);
  });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    scrollRef.current.current = v;
  });

  const enter = useCallback(() => navigate("/login"), [navigate]);
  const tryDemo = useCallback(() => navigate("/demo"), [navigate]);

  return (
    <div className="relative bg-black overflow-x-hidden">
      {/* Scroll distance */}
      <div style={{ height: "500vh" }} className="pointer-events-none" />

      {/* 3D scene — always visible */}
      <LeverScene scrollRef={scrollRef} />

      <FilmGrain />
      <Vignette />

      {/* ── HERO ── */}
      <motion.div
        style={{ opacity: heroOpacity, y: heroY }}
        className="fixed inset-0 z-[6] flex flex-col pointer-events-none"
      >
        <header className="flex items-center justify-between px-5 py-5 sm:px-10 md:px-14 pointer-events-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={mounted ? { opacity: 1 } : undefined}
            transition={{ duration: 1, delay: 0.1 }}
            className="flex items-center gap-2.5 text-white"
          >
            <LeverMark size={15} />
            <span
              className="text-[13px] font-semibold tracking-[0.01em] text-white/60"
              style={{ fontFamily: "var(--font-display)" }}
            >
              lever
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={mounted ? { opacity: 1 } : undefined}
            transition={{ duration: 1, delay: 0.2 }}
            className="pointer-events-auto"
          >
            <button
              onClick={enter}
              className="text-[11px] font-medium tracking-[0.06em] uppercase text-white/50
                hover:text-white/80 transition-colors duration-300"
              style={{ fontFamily: "var(--font-mono)" }}
            >
              Sign in
            </button>
          </motion.div>
        </header>

        <div className="flex-1" />

        <div className="px-5 pb-8 sm:px-10 sm:pb-12 md:px-14 md:pb-16">
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={mounted ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: 1.2, delay: 0.3, ease: EASE_EXPO }}
            className="text-white select-none"
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: "clamp(56px, 15vw, 220px)",
              lineHeight: 0.85,
              letterSpacing: "-0.05em",
            }}
          >
            LEVER
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={mounted ? { opacity: 1 } : undefined}
            transition={{ duration: 1.2, delay: 0.7 }}
            className="text-white/50 max-w-xs mt-4 sm:mt-6"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "10px",
              fontWeight: 500,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              lineHeight: 1.7,
            }}
          >
            Technical editor &amp; writing tool
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={mounted && !scrolled ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="mt-12 sm:mt-20"
          >
            <motion.div
              animate={{ scaleY: [0.4, 1, 0.4] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="w-px h-10 bg-gradient-to-b from-white/15 to-transparent origin-top"
            />
          </motion.div>
        </div>
      </motion.div>

      {/* ── NAV ── */}
      <motion.header
        style={{
          opacity: navOpacity,
          pointerEvents: pastHero ? "auto" : "none",
        }}
        className="fixed top-0 left-0 right-0 z-[10] flex items-center justify-between
          px-5 sm:px-10 md:px-14 py-4
          bg-black/80 border-b border-white/[0.04]"
      >
        <div className="flex items-center gap-2.5 text-white">
          <LeverMark size={14} />
          <span
            className="text-[12px] font-semibold tracking-[0.01em] text-white/50"
            style={{ fontFamily: "var(--font-display)" }}
          >
            lever
          </span>
        </div>
        <button
          onClick={enter}
          className="text-[10px] font-medium tracking-[0.08em] uppercase text-white/50
            hover:text-white/80 transition-colors duration-300"
          style={{ fontFamily: "var(--font-mono)" }}
        >
          Sign in
        </button>
      </motion.header>

      {/* ── STATEMENT 1 — bottom-left ── */}
      <motion.div
        style={{ opacity: s1.opacity, willChange: "opacity" }}
        className="fixed z-[5] bottom-[14vh] left-5 sm:left-10 md:left-14
          pointer-events-none max-w-[85vw] sm:max-w-[60vw] md:max-w-xl"
      >
        <p
          className="text-white select-none"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 600,
            fontSize: "clamp(26px, 5vw, 56px)",
            lineHeight: 1.1,
            letterSpacing: "-0.03em",
          }}
        >
          The instrument for thought.
        </p>
      </motion.div>

      {/* ── STATEMENT 2 — right side ── */}
      <motion.div
        style={{ opacity: s2.opacity, willChange: "opacity" }}
        className="fixed z-[5] top-[32vh] right-5 sm:right-10 md:right-14
          pointer-events-none max-w-[85vw] sm:max-w-[55vw] md:max-w-lg text-right"
      >
        <p
          className="text-white select-none"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 600,
            fontSize: "clamp(26px, 5vw, 56px)",
            lineHeight: 1.1,
            letterSpacing: "-0.03em",
          }}
        >
          LaTeX, Markdown, AI&mdash;unified.
        </p>
      </motion.div>

      {/* ── WRITE — top-left ── */}
      <motion.div
        style={{ opacity: c1.opacity, willChange: "opacity" }}
        className="fixed z-[5] top-[22vh] left-5 sm:left-10 md:left-14
          pointer-events-none"
      >
        <span
          className="block text-white/35 mb-3"
          style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.1em" }}
        >
          {CAPS[0].label}
        </span>
        <h3
          className="text-white mb-4"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "clamp(48px, 10vw, 100px)",
            letterSpacing: "-0.04em",
            lineHeight: 0.95,
          }}
        >
          {CAPS[0].title}
        </h3>
        <p
          className="text-white/60 max-w-[280px]"
          style={{ fontFamily: "var(--font-sans)", fontSize: "14px", lineHeight: 1.7 }}
        >
          {CAPS[0].desc}
        </p>
      </motion.div>

      {/* ── THINK — bottom-right ── */}
      <motion.div
        style={{ opacity: c2.opacity, willChange: "opacity" }}
        className="fixed z-[5] bottom-[18vh] right-5 sm:right-10 md:right-14
          pointer-events-none text-right"
      >
        <span
          className="block text-white/35 mb-3"
          style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.1em" }}
        >
          {CAPS[1].label}
        </span>
        <h3
          className="text-white mb-4"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "clamp(48px, 10vw, 100px)",
            letterSpacing: "-0.04em",
            lineHeight: 0.95,
          }}
        >
          {CAPS[1].title}
        </h3>
        <p
          className="text-white/60 max-w-[280px] ml-auto"
          style={{ fontFamily: "var(--font-sans)", fontSize: "14px", lineHeight: 1.7 }}
        >
          {CAPS[1].desc}
        </p>
      </motion.div>

      {/* ── COLLABORATE — bottom-left ── */}
      <motion.div
        style={{ opacity: c3.opacity, willChange: "opacity" }}
        className="fixed z-[5] bottom-[14vh] left-5 sm:left-10 md:left-14
          pointer-events-none"
      >
        <span
          className="block text-white/35 mb-3"
          style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.1em" }}
        >
          {CAPS[2].label}
        </span>
        <h3
          className="text-white mb-4"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "clamp(48px, 10vw, 100px)",
            letterSpacing: "-0.04em",
            lineHeight: 0.95,
          }}
        >
          {CAPS[2].title}
        </h3>
        <p
          className="text-white/60 max-w-[280px]"
          style={{ fontFamily: "var(--font-sans)", fontSize: "14px", lineHeight: 1.7 }}
        >
          {CAPS[2].desc}
        </p>
      </motion.div>

      {/* ── QUOTE — centered ── */}
      <motion.div
        style={{ opacity: quote.opacity, willChange: "opacity" }}
        className="fixed z-[5] inset-0 flex items-center justify-center
          pointer-events-none px-8"
      >
        <blockquote className="max-w-2xl text-center">
          <p
            className="text-white/70 select-none"
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 500,
              fontSize: "clamp(18px, 3.5vw, 30px)",
              lineHeight: 1.5,
              letterSpacing: "-0.02em",
              fontStyle: "italic",
            }}
          >
            &ldquo;Give me a lever long enough and a fulcrum on which to place
            it, and I shall move the world.&rdquo;
          </p>
          <cite
            className="block mt-6 text-white/35 not-italic"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "10px",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            Archimedes
          </cite>
        </blockquote>
      </motion.div>

      {/* ── CTA — centered, enters and stays through the end ── */}
      <motion.div
        style={{ opacity: cta.opacity, willChange: "opacity" }}
        className="fixed z-[5] inset-0 flex flex-col items-center justify-center
          pointer-events-none px-5"
      >
        <div className="flex flex-col items-center gap-10 pointer-events-auto">
          <div className="text-white">
            <LeverMark size={28} />
          </div>

          <button
            onClick={enter}
            className="lever-cta-btn px-10 py-3.5 text-white/70
              hover:text-white
              active:scale-[0.97]"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "11px",
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Start writing
          </button>

          <button
            onClick={tryDemo}
            className="mt-5 text-white/50 hover:text-white/70 active:scale-[0.97]
              transition-colors duration-300"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "9px",
              fontWeight: 500,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            or try the demo
          </button>
        </div>

        <p
          className="absolute bottom-8 text-white/20"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "9px",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          &copy; {new Date().getFullYear()} {APP_NAME}
        </p>
      </motion.div>

      {/* ── Scoped styles ── */}
      <style>{`
        @property --border-angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        .lever-cta-btn {
          position: relative;
          border: 1px solid rgba(255,255,255,0.15);
          background: transparent;
          transition: color 0.3s, border-color 0.35s ease;
        }
        .lever-cta-btn::before {
          content: '';
          position: absolute;
          inset: -1px;
          padding: 1px;
          background: conic-gradient(
            from var(--border-angle),
            rgba(255,255,255,0.08) 0%,
            rgba(255,255,255,0.70) 20%,
            rgba(255,255,255,0.08) 40%,
            rgba(255,255,255,0.08) 100%
          );
          -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 0.45s ease;
          animation: border-spin 2.8s linear infinite;
          pointer-events: none;
        }
        .lever-cta-btn:hover {
          border-color: transparent;
        }
        .lever-cta-btn:hover::before {
          opacity: 1;
        }
        @keyframes border-spin {
          to { --border-angle: 360deg; }
        }
        html.lever-landing-active {
          overscroll-behavior-y: none;
          overflow-x: hidden;
          background: #000;
          scrollbar-color: rgba(255,255,255,0.12) transparent;
        }
        html.lever-landing-active body {
          overflow-x: hidden;
          background: #000;
        }
        html.lever-landing-active::-webkit-scrollbar {
          width: 6px !important;
        }
        html.lever-landing-active::-webkit-scrollbar-track {
          background: transparent !important;
        }
        html.lever-landing-active::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.12) !important;
          border-radius: 0 !important;
        }
        html.lever-landing-active::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.25) !important;
          border-radius: 0 !important;
        }
      `}</style>
    </div>
  );
}
